import './ConsoleNavMenu.css';
import 'leaflet/dist/leaflet.css';
import '../console/ConsoleMap.css'
import { useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react'
import { logger } from '../../utils/logger'
import L from "leaflet";
import useDroneHubConnection from '../../hooks/DroneHubConnection';
import { ModemData, ModemPositionData, ModemPositionListSchema, ModemTypeData, ModemTypeListSchema } from '../../api/Drone';
import { APIRequestGet } from '../../utils/APIRequests';
import { HubMapPositionMessage, HubSetOnlineMessage } from '../../asp/DroneHub';
import ConsoleMapMarker, { MarkerData } from './ConsoleMapMarker';
import {
    GPSMapCopterIcon,
    GPSMapCopterOfflineIcon,
    GPSMapPlaneMapIcon,
    GPSMapPlaneOfflineMapIcon
} from '../../assets/icons/GPSMapIcons';
import GoogleMap from '../../maps/GoogleMap';

interface ConsoleMapProps {
    currentDrone: ModemData | null;
    updateFocus: boolean;
    markerClick: (imei: string) => void;
}

function ConsoleMap({ currentDrone, updateFocus, markerClick }: ConsoleMapProps): ReactNode {
    const droneHub = useDroneHubConnection();

    //const [map, setMap] = useState(null)
    const mapRef = useRef<L.Map>(null);
    const [positions, setPositions] = useState<ModemPositionData[]>([]);
    const [drone, setDrone] = useState<ModemData | null>(currentDrone);
    const [types, setTypes] = useState<ModemTypeData[]>([]);

    const typeMap = useMemo(() => {
        const map = new Map();
        types.forEach(item => {
            map.set(item.imei, item.type);
        });
        return map;
    }, [types]);

    useEffect(() => {
        logger.debug("Console Map mount");

        APIRequestGet('/drone/getModemsPosition', {
            schema: ModemPositionListSchema,
            logSuccess: true
        }).then((result) => {
            logger.info(result.data);
            setPositions(result.data);
        }).catch((error) => {
            // 401 разбирает интерцептор axiosInstance, он же и редиректит.
            logger.error(error);
        });

        APIRequestGet('/drone/getModemsType', {
            schema: ModemTypeListSchema,
            logSuccess: true
        }).then((result) => {
            logger.info(result.data);
            setTypes(result.data);
        });
    }, []);

    useEffect(() => {
        if ((drone?.imei && currentDrone?.imei && (drone.imei !== currentDrone.imei)) || drone === null) {
            setDrone(currentDrone);
        }

        if ((drone?.imei && currentDrone?.imei) || drone === null) {
            const exists = positions.some((item) => currentDrone && (item.imei === currentDrone.imei));
            if (exists) {
                const pos = positions.find((item) => currentDrone && (item.imei === currentDrone.imei));
                if (mapRef.current && pos) {
                    mapRef.current.setView([pos.lat, pos.lng]);
                }
            }
        }
    }, [currentDrone, updateFocus]);

    useEffect(() => {
        droneHub.connection.on('MapPosition', (message) => {
            //{imei: '1', lat: 46.964493, lng: 142.75, yaw: 276.95953}
            updatePosition(message);
        });

        droneHub.connection.on('SetOnline', (message) => {
            setOnline(message);
        });
    }, [droneHub]);

    const updatePosition = useCallback((msg: HubMapPositionMessage): void => {
        setPositions((prevMarkers) => {
            const itemExists = prevMarkers.some((item) => item.imei === msg.imei);

            if (itemExists) {
                return prevMarkers.map((marker) =>
                    marker.imei === msg.imei
                        ? { ...marker, lat: msg.lat, lng: msg.lng, yaw: msg.yaw, isOnline: msg.isOnline }
                        : marker
                )
            } else {
                return [...prevMarkers, { imei: msg.imei, lat: msg.lat, lng: msg.lng, yaw: msg.yaw, isOnline: msg.isOnline }];
            }
        });
    }, []);

    const setOnline = useCallback((msg: HubSetOnlineMessage): void => {
        setPositions((prevMarkers) => {
            const itemExists = prevMarkers.some((item) => item.imei === msg.imei);

            if (itemExists) {
                return prevMarkers.map((marker) =>
                    marker.imei === msg.imei
                        ? { ...marker, lat: marker.lat, lng: marker.lng, yaw: marker.yaw, isOnline: msg.isOnline }
                        : marker
                )
            }
            return prevMarkers;
        });
    }, []);

    const handleMarkerClick = useCallback((marker: MarkerData): void => {
        markerClick(marker.imei);
    }, [markerClick]);

    // const callback = (id, phase, actualTime, baseTime, startTime, commitTime) => {
    //     logger.info(`${id}'s ${phase} phase: Actual: ${actualTime} ms`);
    // }

    const getIcon = (imei: string, isOnline: boolean): L.Icon => {
        const type = typeMap.get(imei);
        if (type === 1) return isOnline ? GPSMapPlaneMapIcon : GPSMapPlaneOfflineMapIcon;
        return isOnline ? GPSMapCopterIcon : GPSMapCopterOfflineIcon;
    };

    const renderedMarkers = useMemo(
        () => {
            return positions.map((marker) => (
                <ConsoleMapMarker
                    key={marker.imei}
                    marker={marker}
                    icon={getIcon(marker.imei, marker.isOnline)}
                    onClick={handleMarkerClick}
                    isOnline={marker.isOnline}
                />
            ))
        },
        [positions, updatePosition, currentDrone, types, markerClick]
    );

    return (
        <div className='console-map-container'>
            <div className={"map google-map"} style={{ width: "100%", height: "100%" }}>
                <GoogleMap center={[51.990465, 41.158602]} zoom={7} whenCreated={(map) => (mapRef.current = map)}>
                    {renderedMarkers}
                </GoogleMap>
            </div>
        </div>
    )
}

export default ConsoleMap;